import {useMemo} from 'react'
import useConfig from '@salesforce/commerce-sdk-react/hooks/useConfig'
import {useQuery} from '@salesforce/commerce-sdk-react/hooks/useQuery'
import {
    mergeOptions,
    omitNullableParameters,
    pickValidParams
} from '@salesforce/commerce-sdk-react/hooks/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {ShopperExperience} from 'commerce-sdk-isomorphic'
import {keysToCamel} from '../commerce-api/utils'
import useMultiSite from './use-multi-site'

/**
 * Fetches content assets through the standard SCAPI Shopper Experience getContent endpoint.
 *
 * @example
 * const {data} = useShopperContentAsset({parameters: {cid: 'pwa_footer'}})
 *
 * @param {{parameters: {cid: string}}} apiOptions
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult}
 */
export const useShopperContentAsset = (apiOptions = {}, queryOptions) => {
    const {cid, ...rest} = apiOptions.parameters || {}
    const {locale, site} = useMultiSite()
    const config = useConfig()
    const client = useMemo(
        () =>
            new ShopperExperience({
                proxy: config.proxy,
                headers: config.headers,
                parameters: {
                    clientId: config.clientId,
                    organizationId: config.organizationId,
                    shortCode: config.shortCode,
                    siteId: config.siteId,
                    locale: config.locale,
                    currency: config.currency
                },
                throwOnBadResponse: true,
                fetchOptions: config.fetchOptions
            }),
        [config]
    )
    const methodName = 'getContent'
    const requiredParameters = ShopperExperience.paramKeys[`${methodName}Required`]

    const netOptions = omitNullableParameters(
        mergeOptions(client, {
            ...apiOptions,
            parameters: {
                id: cid,
                siteId: site.id,
                locale: locale.id,
                ...rest
            }
        })
    )
    const parameters = pickValidParams(
        netOptions.parameters,
        ShopperExperience.paramKeys[methodName]
    )
    const queryKey = [
        '/commerce-sdk-react',
        '/organizations/',
        netOptions.parameters.organizationId,
        '/contents/',
        cid,
        parameters
    ]
    const method = async (options) => await client[methodName](options)

    return useQuery(
        {
            ...netOptions,
            parameters
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            select: (data) => {
                const normalizedData = keysToCamel(data)
                return queryOptions?.select ? queryOptions.select(normalizedData) : normalizedData
            },
            enabled: Boolean(cid && cid !== '-') && queryOptions?.enabled !== false
        },
        {
            method,
            queryKey,
            requiredParameters
        }
    )
}
